import { makeAutoObservable, action, computed } from "mobx";
import toast from "../utils/toast";
import SalesEndpoints from "../api/SalesEndpoints";
import Cookies from "js-cookie";
import config from "../config";
import {
    Cart,
    CartEventPackageItem,
    CartItem,
    GACartItem,
} from "../models/Cart";
import {
    HoldGroupTicketsRequest,
    SimpleDepartment,
    SimpleEvent,
    SimpleEventSeatmap,
    SimpleSeatmapSection,
} from "../models/Event";
import {
    capitalizeWords,
    generateCartKey,
    getErrorMessage,
    getErrorWithData,
} from "../utils/helpers";
import AuthStore from "./AuthStore";
import {
    EcommercePayment,
    IsEcommercePayment,
    IsOfflinePayment,
    IsSquareEcommercePayment,
    OfflinePayment,
    OrderAddress,
    OrderRequest,
    OtherAddress,
    SquareOnlinePayment,
} from "../requests/OrderRequests";
import { Order } from "../models/Order";
import { gtm } from "../utils/gtm";
import { EventPackage } from "../models/AssignedSeating";
import DialogStore from "./DialogStore";
import { AxiosError } from "axios";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
} from "@mui/material";

type CrossSell = {
    message: string;
    link: string;
};

export const GACartItemKey = (item: CartItem) =>
    "" +
    item.department.id +
    (item.comp
        ? item.comp
        : item.redemption !== null
          ? item.redemption.code.toUpperCase()
          : "") +
    (item.discount
        ? `${item.discount.id}-${item.discount.discountAmountId}`
        : "") +
    (item.overridePrice !== null ? item.overridePrice : "") +
    (item.holdGroupSection?.id ?? "");

let store: CartStore | null = null;

class CartStore {
    private _discriminator: string;
    private _loading: boolean = true;
    private _error: string = "";
    private _hasError: boolean = false;
    private _cartKey: {
        dateCreated: number;
        key: string;
    } | null = null;
    private _cart: Cart | null = null;
    private _promoCode: string | null = null;
    private _salesTrackingKey: string | null = null;
    private _sdaCode: string | null = null;
    private _deliveryMethodId: number = 1;
    private _customAmount: number = 0;
    private _ticketRemovedListeners: ((ticketIds: number[]) => void)[] = [];
    private _seatmapTicketRemovedListeners: ((
        seatmapId: number,
        seatId: number,
    ) => void)[] = [];
    private _packageRemovedListeners: ((
        eventPackageId: number,
        seatId: number,
    ) => void)[] = [];

    private _showCrossSellDialog: boolean = true;

    constructor(discriminator: string) {
        makeAutoObservable(this);
        this._discriminator = discriminator.toString().toLowerCase();

        if (typeof window !== "undefined") {
            AuthStore.setOnBoxOfficeChanged(() => this.nextCart(true));
            this._loadCartKey();
            this._loadPromoCode();
            this._loadSalesTrackingKey();
            this.refresh();
        }
    }

    addTicketRemovedListener(listener: (ticketIds: number[]) => void) {
        if (!this._ticketRemovedListeners.includes(listener)) {
            this._ticketRemovedListeners.push(listener);
        }
    }

    removeTicketRemovedListener(listener: (ticketIds: number[]) => void) {
        this._ticketRemovedListeners = this._ticketRemovedListeners.filter(
            (it) => it !== listener,
        );
    }

    triggerTicketsRemoved(ticketIds: number[]) {
        this._ticketRemovedListeners.forEach((it) => it(ticketIds));
    }

    addSeatmapTicketRemovedListener(
        listener: (seatmapId: number, seatId: number) => void,
    ) {
        if (!this._seatmapTicketRemovedListeners.includes(listener)) {
            this._seatmapTicketRemovedListeners.push(listener);
        }
    }

    removeSeatmapTicketRemovedListener(
        listener: (seatmapId: number, seatId: number) => void,
    ) {
        this._seatmapTicketRemovedListeners =
            this._seatmapTicketRemovedListeners.filter((it) => it !== listener);
    }

    triggerSeatmapTicketRemoved(seatmapId: number, seatId: number) {
        this._seatmapTicketRemovedListeners.forEach((it) =>
            it(seatmapId, seatId),
        );
    }

    addPackageRemovedListener(
        listener: (eventPackageId: number, seatId: number) => void,
    ) {
        if (!this._packageRemovedListeners.includes(listener)) {
            this._packageRemovedListeners.push(listener);
        }
    }

    removePackageRemovedListener(
        listener: (eventPackageId: number, seatId: number) => void,
    ) {
        this._packageRemovedListeners = this._packageRemovedListeners.filter(
            (it) => it !== listener,
        );
    }

    triggerPackageRemoved(eventPackageId: number, seatId: number) {
        this._packageRemovedListeners.forEach((it) =>
            it(eventPackageId, seatId),
        );
    }

    @computed
    get cartKey() {
        return this._cartKey;
    }

    @computed
    get cart() {
        return this._cart;
    }

    @computed
    get discriminator() {
        return this._discriminator;
    }

    @computed
    get loading() {
        return this._loading;
    }

    @computed
    get hasError() {
        return this._hasError;
    }

    @computed
    get error() {
        return this._error;
    }

    @computed
    get quantity() {
        if (!this._cart) return 0;
        return this._cart.items.length + this._cart.packageItems.length;
    }

    @computed
    get promoCode() {
        return this._promoCode;
    }

    @computed
    get hasItems() {
        return this.quantity > 0;
    }

    @computed
    get newestItemDate(): number | null {
        if (!this.cart) return null;
        return (
            [
                ...this.cart.items.map((it) => it.dateCreated),
                ...this.cart.packageItems.map((it) => it.dateCreated),
            ].sort(
                (a, b) =>
                    (b ?? Number.MIN_SAFE_INTEGER) -
                    (a ?? Number.MIN_SAFE_INTEGER),
            )[0] ?? null
        );
    }

    @computed
    get oldestItemDate(): number | null {
        if (!this.cart) return null;
        return (
            [
                ...this.cart.items.map((it) => it.dateCreated),
                ...this.cart.packageItems.map((it) => it.dateCreated),
            ].sort(
                (a, b) =>
                    (a ?? Number.MAX_SAFE_INTEGER) -
                    (b ?? Number.MAX_SAFE_INTEGER),
            )[0] ?? null
        );
    }

    @computed
    get cartExpiresInSeconds(): number | null {
        // Return server-provided expiry seconds if available
        return this.cart?.expiresInSeconds ?? null;
    }

    @computed
    get crossSellNode(): CrossSell | null {
        if (!this._cart) return null;

        // Strath cross sell
        if (
            this._hasStrathmoreCowboyTickets() &&
            !this._hasRanchmansConcertTickets()
        ) {
            return {
                message:
                    "Would you like to purchase a bus ride from Ranchman's for the Cowboy Town Concert?",
                link: "https://shop.authentigate.ca/store/strathmore/1398",
            };
        }
        return null;
    }

    @computed
    get showCrossSellDialog() {
        return (
            !AuthStore.loggedIn &&
            this._showCrossSellDialog &&
            !!this.crossSellNode
        );
    }

    @computed
    get gaItems() {
        if (!this._cart) return [];
        return Object.values(
            this._cart.items
                .filter(
                    (it) =>
                        !it.department.assignedSeating &&
                        !it.sectionSeat &&
                        !it.seatmapSection,
                )
                .reduce(
                    (a, b) => {
                        if (a[GACartItemKey(b)]) {
                            a[GACartItemKey(b)].quantity++;
                            a[GACartItemKey(b)].totalAmount +=
                                this.itemTotal(b);
                            a[GACartItemKey(b)].ticketIds.push(b.ticketId);
                        } else {
                            a[GACartItemKey(b)] = {
                                event: b.event,
                                department: b.department,
                                comp: b.comp,
                                acceptsModifications: b.acceptsModifications,
                                ticketIds: [b.ticketId],
                                quantity: 1,
                                applicableTaxIds: b.applicableTaxIds,
                                serviceFees: b.serviceFees,
                                discount: b.discount,
                                redemption: b.redemption,
                                totalAmount: this.itemTotal(b),
                                basePrice: this.itemPrice(b),
                                taxInclusive: b.taxInclusive,
                                grossPrice: b.grossPrice,
                                taxes: b.taxes,
                                overridePrice: b.overridePrice,
                            };
                        }
                        return a;
                    },
                    {} as { [key: string]: GACartItem },
                ),
        ).sort((a, b) => {
            return a.department.id - b.department.id;
        });
    }

    @computed
    get assignedSeatingItems() {
        if (!this._cart) return [];
        return this._cart.items
            .filter((it) => it.department.assignedSeating)
            .sort((a, b) =>
                a.department.id === b.department.id
                    ? (a.seatDetails || "").localeCompare(b.seatDetails || "")
                    : a.department.id - b.department.id,
            );
    }

    @computed
    get assignedSeatingItemsGrouped() {
        if (!this._cart) return [];
        return Object.values(
            this.assignedSeatingItems.reduce(
                (a, b) => {
                    if (a[b.department.id]) {
                        a[b.department.id].items.push(b);
                    } else {
                        a[b.department.id] = {
                            department: b.department,
                            items: [b],
                        };
                    }
                    return a;
                },
                {} as {
                    [key: number]: {
                        department: SimpleDepartment;
                        items: CartItem[];
                    };
                },
            ),
        ).sort((a, b) => a.department.id - b.department.id);
    }

    @computed
    get seatmapItems() {
        if (!this._cart) return [];
        return this._cart.items.filter(
            (it) =>
                (!!it.sectionSeat || !!it.seatmapSection) && !!it.eventSeatmap,
        );
    }

    @computed
    get seatmapItemsGrouped() {
        if (!this._cart) return [];
        return Object.values(
            this._cart.items
                .filter(
                    (it) =>
                        (!!it.sectionSeat || !!it.seatmapSection) &&
                        !!it.eventSeatmap,
                )
                .reduce(
                    (a, b) => {
                        if (a[b.eventSeatmap!.id]) {
                            if (b.sectionSeat) {
                                a[b.eventSeatmap!.id].seats.push(b);
                            } else if (b.seatmapSection) {
                                if (
                                    a[b.eventSeatmap!.id].sections[
                                        b.seatmapSection.id
                                    ]
                                ) {
                                    a[b.eventSeatmap!.id].sections[
                                        b.seatmapSection.id
                                    ].items.push(b);
                                } else {
                                    a[b.eventSeatmap!.id].sections[
                                        b.seatmapSection.id
                                    ] = {
                                        section: b.seatmapSection,
                                        items: [b],
                                    };
                                }
                            }
                        } else {
                            a[b.eventSeatmap!.id] = {
                                eventSeatmap: b.eventSeatmap!,
                                seats: [],
                                sections: {},
                            };
                            if (b.sectionSeat) {
                                a[b.eventSeatmap!.id].seats.push(b);
                            } else if (b.seatmapSection) {
                                a[b.eventSeatmap!.id].sections[
                                    b.seatmapSection.id
                                ] = {
                                    section: b.seatmapSection,
                                    items: [b],
                                };
                            }
                        }
                        return a;
                    },
                    {} as {
                        [key: number]: {
                            eventSeatmap: SimpleEventSeatmap;
                            seats: CartItem[];
                            sections: {
                                [key: number]: {
                                    section: SimpleSeatmapSection;
                                    items: CartItem[];
                                };
                            };
                        };
                    },
                ),
        ).sort((a, b) => a.eventSeatmap.id - b.eventSeatmap.id);
    }

    @computed
    get packageItems() {
        if (!this._cart) return [];
        return this._cart.packageItems;
    }

    @computed
    get packageItemsGrouped() {
        return Object.values(
            this.packageItems.reduce(
                (a, b) => {
                    if (a[b.eventPackage.id]) {
                        a[b.eventPackage.id].items.push(b);
                    } else {
                        a[b.eventPackage.id] = {
                            eventPackage: b.eventPackage,
                            items: [b],
                        };
                    }
                    return a;
                },
                {} as {
                    [key: number]: {
                        eventPackage: EventPackage;
                        items: CartEventPackageItem[];
                    };
                },
            ),
        ).sort((a, b) => a.eventPackage.id - b.eventPackage.id);
    }

    @computed get hasRequiredDepartments() {
        return this.requiredDepartments.length > 0;
    }

    @computed
    get requiredDepartments() {
        if (!this._cart) return [];

        const departmentQuantityMap = this._cart.items
            .map((item) => item.department.id)
            .reduce((a: any, b) => {
                if (a[b]) {
                    a[b]++;
                } else a[b] = 1;
                return a;
            }, {});

        if (!departmentQuantityMap[33565]) {
            return [];
        }

        const eventQuantityMap = this._cart.items
            .filter((item) => item.department.id !== 33565)
            .map((item) => item.event.id)
            .reduce((a: any, b) => {
                if (a[b]) {
                    a[b]++;
                } else a[b] = 1;
                return a;
            }, {});

        const missingEvents: Array<any> = [];
        this._cart.items.forEach((item) => {
            if (item.department.id !== 33565) {
                return;
            }
            const required = departmentQuantityMap[item.department.id];
            const present = eventQuantityMap[item.event.id] || 0;
            if (
                required > present &&
                !missingEvents.find((e) => e.id === item.event.id)
            ) {
                missingEvents.push({
                    id: item.event.id,
                    title: item.event.title,
                    departmentName: item.department.name,
                });
            }
        });
        return missingEvents;
    }

    @computed
    get hasRequiredEvents() {
        return this.requiredEvents.length > 0;
    }

    @computed
    get requiredEvents() {
        if (!this._cart) return [];

        const eventQuantityMap = this._cart.items
            .map((item) => item.event.id)
            .reduce((a: any, b) => {
                if (a[b]) {
                    a[b]++;
                } else a[b] = 1;
                return a;
            }, {});

        const missingEvents: Array<SimpleEvent> = [];
        this._cart.items.forEach((item) => {
            if (!item.event.requiredEvent) {
                return;
            }
            const required = eventQuantityMap[item.event.id];
            const present = eventQuantityMap[item.event.requiredEvent.id] || 0;
            if (
                required > present &&
                !missingEvents.find((e) => e.id === item.event.id)
            ) {
                missingEvents.push(item.event);
            }
        });

        return missingEvents;
    }

    @computed
    get usesCustomFields() {
        return (
            this.gaItems.some((it) => it.department.customFields.length > 0) ||
            this.assignedSeatingItems.some(
                (it) => it.department.customFields.length > 0,
            )
        );
    }

    @computed
    get usesShippingAddress() {
        return (
            this.gaItems.some((it) => it.department.usesShippingAddress) ||
            this.assignedSeatingItems.some(
                (it) => it.department.usesShippingAddress,
            )
        );
    }

    @computed
    get ticketSubtotal() {
        if (!this._cart) return 0;
        return (
            this._cart.items.reduce((a, b) => a + this.itemTotal(b), 0) +
            this._cart.packageItems.reduce(
                (a, b) => a + this.packageItemTotal(b),
                0,
            )
        );
    }

    @computed
    get ticketBaseSubtotal() {
        if (!this._cart) return 0;
        return (
            this._cart.items.reduce((a, b) => a + this.itemPrice(b), 0) +
            this._cart.packageItems.reduce((a, b) => a + this.itemPrice(b), 0)
        );
    }

    @computed
    get subtotal() {
        if (!this._cart) return 0;
        return (
            this._cart.items.reduce((a, b) => a + this.itemTotal(b), 0) +
            this._cart.packageItems.reduce(
                (a, b) => a + this.packageItemTotal(b),
                0,
            ) +
            this._cart.authFee +
            (this._cart.deliveryMethod?.amount ?? 0) +
            (this._cart.customAmount ?? 0)
        );
    }

    @computed
    get customAmount(): number {
        return this._cart?.customAmount ?? this._customAmount;
    }

    @action
    setCustomAmount(amount: number) {
        this._customAmount = Math.max(0, Math.round(amount));
        this.refresh();
    }

    @computed
    get totalTaxes() {
        if (!this._cart) return 0;
        return this._cart.taxes.reduce((a, b) => a + b.amount, 0);
    }

    @computed
    get total() {
        if (!this._cart) return 0;
        return this.subtotal + this.totalTaxes;
    }

    @computed
    get authFee() {
        if (!this._cart) return 0;
        return this._cart.authFee;
    }

    @computed
    get taxes() {
        if (!this._cart) return [];
        return this._cart.taxes;
    }

    @computed
    get deliveryMethod() {
        if (!this._cart) return null;
        return this._cart.deliveryMethod;
    }

    @action
    changeDeliveryMethod(methodId: number) {
        this._deliveryMethodId = methodId;
        this.refresh();
    }

    private get _customAmountArg(): number | undefined {
        return this._customAmount > 0 ? this._customAmount : undefined;
    }

    @action
    async refresh(finishLoading: boolean = true) {
        try {
            this._loading = true;
            const data = await SalesEndpoints.getCart(
                this._cartKey!.key,
                this._promoCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            // this._updatePromoCodeFromCart(this._cart);
            gtm.trackCartRefresh();
        } catch (err) {
            console.log(err);
        } finally {
            if (finishLoading) {
                this._loading = false;
            }
        }
    }

    @action
    async nextCart(clearToo: boolean = false) {
        try {
            this._loading = true;
            if (clearToo) {
                try {
                    await this.clearCart();
                } catch (_) {
                    console.log(_);
                }
            }
            this._loadCartKey(true);
            this._savePromoCode("");
            this._deliveryMethodId = 1;
            this._customAmount = 0;
            this.refresh();
        } catch (err) {
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async clearCart(showToast: boolean = true) {
        try {
            const oldCart = Object.assign({}, this._cart);
            this._loading = true;
            const data = await SalesEndpoints.clearCart(this._cartKey!.key);
            this._cart = data;
            this._checkRemovedTicketsFromCart(oldCart, this._cart);
            if (showToast) {
                toast.success("Cart has been cleared.");
            }
            this._deliveryMethodId = 1;
            this._customAmount = 0;
            gtm.trackCartCleared(oldCart);
        } catch (err) {
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async completeOrder(
        orderAddress: OrderAddress,
        payment: EcommercePayment | OfflinePayment | SquareOnlinePayment,
        shippingAddress?: OtherAddress,
        metadata: { [key: string]: any } = {},
        ticketFields: { [key: number]: any } = {},
        orderFields: {
            fieldId: number;
            value: any;
        }[] = [],
        donations: {
            [key: number]: number;
        } = {},
    ): Promise<Order> {
        if (
            !this._cart ||
            !this._cartKey ||
            (this._cart.items.length === 0 &&
                this._cart.packageItems.length === 0)
        )
            throw new Error("Cart is empty.");

        const request: OrderRequest = {
            cartKey: this._cartKey!.key,
            sanityCheck: {
                ticketIds: this._cart.items.map((it) => it.ticketId),
                eventPackageStatusIds: this._cart.packageItems.map(
                    (it) => it.id,
                ),
                expectedSubTotal: this.subtotal,
                expectedTotal:
                    this.total +
                    Object.values(donations).reduce((a, b) => a + b, 0),
            },
            address: orderAddress,
            shippingAddress,
            discountCode: this._promoCode || "",
            metadata: {
                ...metadata,
                salesTrackingKey: this._salesTrackingKey ?? undefined,
            },
            ticketFields,
            deliveryMethodId: this._deliveryMethodId,
            customAmount: this._customAmountArg,
            orderFields,
            donations,
        };

        if (this._sdaCode) {
            request.sdaCode = this._sdaCode;
        }

        if (AuthStore.loggedIn && AuthStore.boxoffice) {
            if (!IsOfflinePayment(payment)) {
                throw Error("Box office orders must be offline payments.");
            }
            request.offlinePayment = payment;
        } else if (IsEcommercePayment(payment)) {
            request.ecommercePayment = payment;
        } else if (IsSquareEcommercePayment(payment)) {
            request.squareOnlinePayment = payment;
        } else {
            throw Error("Invalid payment type.");
        }

        this._loading = true;

        try {
            const order = await SalesEndpoints.completeOrder(request);
            return order;
        } catch (err) {
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    setSdaCode(code: string) {
        this._sdaCode = code;
    }

    @computed
    get sdaCode() {
        return this._sdaCode;
    }

    @action
    async applyPromoCode(code: string) {
        this._loading = true;
        try {
            const cart = await SalesEndpoints.checkDiscountCode(
                this.discriminator,
                this.cartKey?.key ?? "",
                code,
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._promoCode = code;
            this._savePromoCode(code);
            this._cart = cart;
            toast.success("Promo code applied.");
        } catch (err) {
            toast.error(getErrorMessage(err, "Failed to apply promo code."));
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async removePromoCode() {
        this._promoCode = "";
        this._savePromoCode("");
        await this.refresh();
    }

    @action
    setSalesTrackingKey(key: string) {
        this._salesTrackingKey = key;
        this._saveSalesTrackingKey(key);
    }

    @action
    removeSalesTrackingKey() {
        this._salesTrackingKey = null;
        localStorage.removeItem(`sales-tracking-key-${this.discriminator}`);
    }

    @action
    async applyRedemptionCode(code: string) {
        try {
            this._loading = true;
            const data = await SalesEndpoints.applyRedemptionCode(
                this._cartKey!.key,
                code,
                this._promoCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Redemption code applied.");
        } catch (err) {
            toast.error(
                getErrorMessage(err, "Failed to apply redemption code."),
            );
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async removeRedemptionCode(codeId: number) {
        try {
            this._loading = true;
            const data = await SalesEndpoints.removeRedemptionCode(
                this._cartKey!.key,
                codeId,
                this._promoCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Redemption code removed.");
        } catch (err) {
            toast.error(
                getErrorMessage(err, "Failed to remove redemption code."),
            );
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async incrementGaQuantity(
        departmentId: number,
        comp: boolean = false,
        department?: SimpleDepartment,
        redemptionCodeId?: number,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        const items = this._cart.items.filter(
            (i) =>
                i.department.id === departmentId &&
                i.comp === comp &&
                ((!i.redemption && !redemptionCodeId) ||
                    (i.redemption && i.redemption.id === redemptionCodeId)),
        );

        const quantity = items.length + 1;

        this._loading = true;

        try {
            const data = await SalesEndpoints.updateGaQuantity(
                this._cartKey.key,
                { departmentId, quantity, comp, redemptionCodeId },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket(s) added to cart.");
            if (department) {
                gtm.trackItemAddedToCart(department, comp, 1);
            }
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async decrementGaQuantity(
        departmentId: number,
        comp: boolean = false,
        department?: SimpleDepartment,
        redemptionCodeId?: number,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        const items = this._cart.items.filter(
            (i) =>
                i.department.id === departmentId &&
                i.comp === comp &&
                ((!i.redemption && !redemptionCodeId) ||
                    (i.redemption && i.redemption.id === redemptionCodeId)),
        );

        const quantity = Math.max(items.length - 1, 0);

        this._loading = true;

        try {
            const data = await SalesEndpoints.updateGaQuantity(
                this._cartKey.key,
                { departmentId, quantity, comp, redemptionCodeId },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.error("Ticket(s) removed from cart.");
            if (department) {
                gtm.trackItemRemovedFromCart(department, comp, 1);
            }
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async setGaQuantity(
        departmentId: number,
        comp: boolean = false,
        quantity: number,
        department: SimpleDepartment | undefined,
        redemptionCodeId?: number,
        showToast?: boolean,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.updateGaQuantity(
                this._cartKey.key,
                { departmentId, quantity, comp, redemptionCodeId },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            if (showToast !== false) {
                toast.success("Cart updated.");
            }
        } catch (err: any) {
            toast.error(getErrorMessage(err));
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async addTicketsToCart(
        tickets: number[],
        comp: boolean = false,
        department?: SimpleDepartment,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.addTicketsToCart(
                this._cartKey.key,
                { tickets, comp },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket(s) added to cart.");
        } catch (err: any) {
            toast.error(getErrorMessage(err));
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @action
    async addReservedTicketsToCart(slug: string) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.addReservedTicketsToCart(
                this._cartKey.key,
                slug,
            );
            this._cart = data;
            toast.success("Season ticket(s) added to cart.");
        } catch (err: any) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async addHoldGroupTicketsToCart(
        holdGroupId: number,
        request: HoldGroupTicketsRequest,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.addHoldGroupTicketsToCart(
                this._cartKey.key,
                holdGroupId,
                request,
                this._promoCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket(s) added to cart.");
        } catch (err: any) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async addSeatmapTicketsToCart(
        eventSeatmapId: number,
        seatId: number,
        departmentId: number,
        holdGroupSeatId: number | undefined,
        comp: boolean = false,
        department?: SimpleDepartment,
        confirmAdd: boolean = false,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.addSeatmapTicketToCart(
                this._cartKey.key,
                {
                    eventSeatmapId,
                    seatId,
                    departmentId,
                    holdGroupSeatId,
                    comp,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                confirmAdd,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket(s) added to cart.");
        } catch (err: any) {
            if (typeof err.response?.data === "object") {
                const errorMessage = getErrorWithData(err.response.data);
                if (errorMessage.errorCode === "CONFIRM_ADD") {
                    DialogStore.showAlert(
                        errorMessage.data.title,
                        errorMessage.message,
                        () => {
                            this.addSeatmapTicketsToCart(
                                eventSeatmapId,
                                seatId,
                                departmentId,
                                holdGroupSeatId,
                                comp,
                                department,
                                true,
                            ).catch(() => {});
                        },
                        "Add All Seats to Cart",
                        "No Thanks",
                        () => (
                            <Grid container spacing={1}>
                                {errorMessage.data.seats.map((seat: any) => (
                                    <Grid item key={seat.id} xs={12}>
                                        <Card
                                            sx={(theme) => ({
                                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                            })}
                                        >
                                            <CardContent>
                                                <Typography variant="h5">
                                                    {seat.seatDetails}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ),
                    );
                }
            } else {
                const errorMessage = getErrorMessage(err);
                if (errorMessage.startsWith("CONFIRM_ADD")) {
                    const sectionType = capitalizeWords(
                        errorMessage.split(":")[1],
                    );
                    DialogStore.showAlert(
                        `${sectionType} Confirmation`,
                        `This ${sectionType} requires all seats to be purchased together. Would you like to add all seats to your cart?`,
                        () => {
                            this.addSeatmapTicketsToCart(
                                eventSeatmapId,
                                seatId,
                                departmentId,
                                holdGroupSeatId,
                                comp,
                                department,
                                true,
                            ).catch(() => {});
                        },
                    );
                } else {
                    toast.error(getErrorMessage(err));
                    console.log(err);
                    throw err;
                }
            }
        } finally {
            this._loading = false;
        }
    }

    @action
    async removeSeatmapTicketFromCart(eventSeatmapId: number, seatId: number) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;
        this._loading = true;

        try {
            const originalCart = Object.assign({}, this._cart);
            const data = await SalesEndpoints.removeSeatmapTicketFromCart(
                this._cartKey.key,
                { eventSeatmapId, seatId },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            this._checkRemovedTicketsFromCart(originalCart, this._cart);
            toast.success("Ticket removed from cart.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async addSeatmapSectionToCart(
        eventSeatmapId: number,
        sectionId: number,
        departmentId: number,
        comp: boolean = false,
        holdGroupSectionId: number | undefined = undefined,
        redemptionCodeId: number | undefined = undefined,
        department?: SimpleDepartment,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        const items = this._cart.items.filter(
            (i) =>
                i.eventSeatmap?.id === eventSeatmapId &&
                i.seatmapSection?.id === sectionId &&
                i.department.id === departmentId &&
                i.holdGroupSection?.id === holdGroupSectionId &&
                i.comp === comp &&
                i.redemption?.id === redemptionCodeId,
        );

        try {
            const data = await SalesEndpoints.setSeatmapSectionQtyInCart(
                this._cartKey.key,
                {
                    eventSeatmapId,
                    sectionId,
                    departmentId,
                    holdGroupSectionId,
                    comp,
                    quantity: items.length + 1,
                    redemptionCodeId,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket added to cart.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async removeSeatmapSectionFromCart(
        eventSeatmapId: number,
        sectionId: number,
        departmentId: number,
        comp: boolean = false,
        holdGroupSectionId: number | undefined = undefined,
        redemptionCodeId: number | undefined = undefined,
        department?: SimpleDepartment,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        const items = this._cart.items.filter(
            (i) =>
                i.eventSeatmap?.id === eventSeatmapId &&
                i.seatmapSection?.id === sectionId &&
                i.department.id === departmentId &&
                i.holdGroupSection?.id === holdGroupSectionId &&
                i.comp === comp &&
                i.redemption?.id === redemptionCodeId,
        );

        if (items.length === 0) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.setSeatmapSectionQtyInCart(
                this._cartKey.key,
                {
                    eventSeatmapId,
                    sectionId,
                    departmentId,
                    holdGroupSectionId,
                    comp,
                    quantity: items.length - 1,
                    redemptionCodeId,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.error("Ticket removed from cart.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async setSeatmapSectionQtyInCart(
        eventSeatmapId: number,
        sectionId: number,
        departmentId: number,
        quantity: number,
        comp: boolean = false,
        holdGroupSectionId: number | undefined = undefined,
        department?: SimpleDepartment,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        if (quantity < 0) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.setSeatmapSectionQtyInCart(
                this._cartKey.key,
                {
                    eventSeatmapId,
                    sectionId,
                    departmentId,
                    holdGroupSectionId,
                    comp,
                    quantity,
                    redemptionCodeId: undefined,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.info("Cart updated.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async addPackageSectionToCart(
        eventPackageId: number,
        sectionId: number,
        priceGroupId: number,
        comp: boolean = false,
        holdGroupEventPackageId?: number | null,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        const items = this._cart.packageItems.filter(
            (i) =>
                i.eventPackage.id === eventPackageId &&
                i.eventPackagePriceGroup.id === priceGroupId &&
                i.sectionId === sectionId &&
                i.comp === comp,
        );

        const quantity = items.length + 1;

        try {
            const data = await SalesEndpoints.setPackageSectionQtyInCart(
                this._cartKey.key,
                {
                    eventPackageId,
                    sectionId,
                    priceGroupId,
                    quantity,
                    comp,
                    holdGroupEventPackageId,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket added to cart.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async removePackageSectionFromCart(
        eventPackageId: number,
        sectionId: number,
        priceGroupId: number,
        comp: boolean = false,
        holdGroupEventPackageId?: number | null,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        const items = this._cart.packageItems.filter(
            (i) =>
                i.eventPackage.id === eventPackageId &&
                i.eventPackagePriceGroup.id === priceGroupId &&
                i.sectionId === sectionId &&
                i.comp === comp,
        );

        const quantity = items.length - 1;

        if (quantity < 0) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.setPackageSectionQtyInCart(
                this._cartKey.key,
                {
                    eventPackageId,
                    sectionId,
                    priceGroupId,
                    quantity,
                    comp,
                    holdGroupEventPackageId,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket added to cart.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async setPackageSectionQtyInCart(
        eventPackageId: number,
        sectionId: number,
        priceGroupId: number,
        quantity: number,
        comp: boolean = false,
        holdGroupEventPackageId?: number | null,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        if (quantity < 0) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.setPackageSectionQtyInCart(
                this._cartKey.key,
                {
                    eventPackageId,
                    sectionId,
                    priceGroupId,
                    quantity,
                    comp,
                    holdGroupEventPackageId,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.info("Cart updated.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async addEventPackageToCart(
        eventPackageId: number,
        seatId: number,
        priceGroupId: number,
        comp: boolean = false,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.addEventPackageToCart(
                this._cartKey.key,
                {
                    eventPackageId,
                    seatId,
                    priceGroupId,
                    comp,
                },
                this._promoCode || "",
                this._sdaCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.success("Ticket added to cart.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async removeEventPackageFromCart(
        statusId: number,
        eventPackageId: number,
        seatId: number,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;
        this._loading = true;

        try {
            const data = await SalesEndpoints.removeEventPackageFromCart(
                this._cartKey.key,
                { statusId },
                this._promoCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            this.triggerPackageRemoved(eventPackageId, seatId);
            toast.success("Ticket removed from cart.");
        } catch (err) {
            toast.error(getErrorMessage(err));
            console.log(err);
            throw err;
        } finally {
            this._loading = false;
        }
    }

    @action
    async removeTicketsFromCart(
        tickets: number[],
        department?: SimpleDepartment,
    ) {
        if (this._loading) return;
        if (!this._cartKey) return;
        if (!this._cart) return;

        this._loading = true;

        try {
            const data = await SalesEndpoints.removeTicketsFromCart(
                this._cartKey.key,
                { tickets },
                this._promoCode || "",
                this._deliveryMethodId,
                this._customAmountArg,
            );
            this._cart = data;
            toast.error("Ticket(s) removed from cart.");
            this.triggerTicketsRemoved(tickets);
        } catch (err) {
            console.log(err);
        } finally {
            this._loading = false;
        }
    }

    @computed
    itemPrice(item: CartItem & any): number {
        // Prefer the server-resolved price: it is net of tax for tax-inclusive
        // departments, which local resolution from department.price cannot know
        if (item.basePrice !== null && item.basePrice !== undefined) {
            return item.basePrice;
        }
        if (item.comp) {
            return 0;
        }
        if (item.redemption) {
            return item.redemption.chargedPrice;
        }
        return item.overridePrice !== null && item.overridePrice !== undefined
            ? item.overridePrice
            : item.holdGroupSection &&
                item.holdGroupSection.overridePrice !== null
              ? item.holdGroupSection.overridePrice
              : item.department.price;
    }

    @computed
    itemTotal(item: CartItem): number {
        // Prefer the server-computed amount: it is this item's exact contribution
        // to the server's subtotal, so the checkout sanity check always matches
        if (
            item.calculatedAmount !== null &&
            item.calculatedAmount !== undefined
        ) {
            return item.calculatedAmount;
        }
        if (item.comp) {
            return 0;
        }
        if (item.redemption) {
            return (
                item.redemption.chargedPrice +
                (item.redemption.feeExempt
                    ? 0
                    : item.serviceFees
                          .map((fee) => fee.amount)
                          .reduce((a, b) => a + b, 0))
            );
        }
        let total =
            (item.overridePrice !== null && item.overridePrice !== undefined
                ? item.overridePrice
                : item.holdGroupSection &&
                    item.holdGroupSection.overridePrice !== null
                  ? item.holdGroupSection.overridePrice
                  : item.department.price) +
            item.serviceFees
                .map((fee) => fee.amount)
                .reduce((a, b) => a + b, 0);
        if (item.discount) {
            total -= item.discount.amount;
        }
        return total;
    }

    @computed
    packageItemTotal(item: CartEventPackageItem): number {
        if (
            item.calculatedAmount !== null &&
            item.calculatedAmount !== undefined
        ) {
            return item.calculatedAmount;
        }
        if (item.comp) {
            return 0;
        }
        let total =
            item.price +
            item.serviceFees
                .map((fee) => fee.amount)
                .reduce((a, b) => a + b, 0);
        if (item.discount) {
            total -= item.discount.amount;
        }
        return total;
    }

    @computed
    get hasAnyCustomFields(): boolean {
        if (!this._cart) return false;
        return this._cart.items.some(
            (i) => i.department.customFields.length > 0,
        );
    }

    @computed
    get itemsWithCustomFields(): CartItem[] {
        if (!this._cart) return [];
        return this._cart.items.filter(
            (i) => i.department.customFields.length > 0,
        );
    }

    @computed
    hasTicket(ticketId: number): boolean {
        if (!this._cart) return false;
        return this._cart.items.some((i) => i.ticketId === ticketId);
    }

    @computed
    hasDepartment(departmentId: number): boolean {
        if (!this._cart) return false;
        return this._cart.items.some((i) => i.department.id === departmentId);
    }

    @computed
    get shouldShowRedemptionCodeForm(): boolean {
        if (!this._cart) return false;
        return this._cart.items.some((i) => [1086, 1095].includes(i.event.id));
    }

    @computed
    get shouldShowPromoCodeForm(): boolean {
        if (!this._cart) return true;

        // Add custom logic here.
        if (this._discriminator.toLowerCase() === "rrex") {
            return false;
        }

        return true;
    }

    @computed
    get shouldShowRefundMessage(): boolean {
        if (!this._cart) return false;
        return this._cart.items.some((i) =>
            [923, 908, 965, 966, 973, 972].includes(i.event.id),
        );
    }

    @computed
    hasEventPackage(eventPackageId: number, seatId: number): boolean {
        if (!this._cart) return false;
        return this._cart.packageItems.some(
            (i) => i.eventPackage.id === eventPackageId && i.seatId === seatId,
        );
    }

    @computed
    getEventPackage(
        eventPackageId: number,
        seatId: number,
    ): CartEventPackageItem | null {
        if (!this._cart) return null;
        return (
            this._cart.packageItems.find(
                (i) =>
                    i.eventPackage.id === eventPackageId && i.seatId === seatId,
            ) ?? null
        );
    }

    @computed
    getEventSeatmapSeat(
        eventSeatmapId: number,
        seatId: number,
    ): CartItem | null {
        if (!this._cart) return null;
        return (
            this._cart.items.find(
                (i) =>
                    i.eventSeatmap?.id === eventSeatmapId &&
                    i.sectionSeat?.id === seatId,
            ) ?? null
        );
    }

    getDiscountUses(id: number): number {
        if (!this._cart) return 0;
        return this._cart.items.filter((i) => i.discount?.id === id).length;
    }

    getDiscountAmountUses(id: number): number {
        if (!this._cart) return 0;
        return this._cart.items.filter(
            (i) => i.discount?.discountAmountId === id,
        ).length;
    }

    getDepartmentQuantity(departmentId: number, comp: boolean): number {
        if (!this._cart) return 0;
        return this._cart.items.filter(
            (i) => i.department.id === departmentId && i.comp === comp,
        ).length;
    }

    getSectionDepartmentQuantity(
        eventSeatmapId: number,
        sectionId: number,
        departmentId: number,
        comp: boolean,
        holdGroupSectionId: number | undefined = undefined,
    ) {
        if (!this._cart) return 0;
        return this._cart.items.filter(
            (i) =>
                i.eventSeatmap?.id === eventSeatmapId &&
                i.seatmapSection?.id === sectionId &&
                i.department.id === departmentId &&
                i.holdGroupSection?.id === holdGroupSectionId &&
                i.comp === comp,
        ).length;
    }

    getPackageSectionQuantity(
        eventPackageId: number,
        sectionId: number,
        priceGroupId: number,
        comp: boolean,
    ) {
        if (!this._cart) return 0;
        return this._cart.packageItems.filter(
            (i) =>
                i.eventPackage.id === eventPackageId &&
                i.sectionId === sectionId &&
                i.eventPackagePriceGroup.id === priceGroupId &&
                i.comp === comp,
        ).length;
    }

    @action
    hideCrossSellDialog() {
        this._showCrossSellDialog = false;
    }

    private _loadCartKey(newKey: boolean = false) {
        const key = `cart-${this.discriminator}`;
        const cart = localStorage.getItem(key);
        if (cart && !newKey) {
            this._cartKey = JSON.parse(cart);
        } else {
            this._cartKey = {
                dateCreated: new Date().getTime(),
                key: generateCartKey(),
            };
            localStorage.setItem(key, JSON.stringify(this._cartKey));
            // Cookies.set(
            //     `__authentigate_t1_cart_key_${this.discriminator}`,
            //     this._cartKey.key,
            //     {
            //         secure: true,
            //     },
            // );
        }
    }

    private _savePromoCode(promoCode: string) {
        // Cookies.set(
        //     `__authentigate_t1_promo_code_${this.discriminator}`,
        //     promoCode,
        //     {
        //         secure: true,
        //     },
        // );
        localStorage.setItem(`promo-code-${this._discriminator}`, promoCode);
    }

    private _loadPromoCode() {
        // this._promoCode =
        //     Cookies.get(`__authentigate_t1_promo_code_${this.discriminator}`) ??
        //     "";
        this._promoCode = localStorage.getItem(
            `promo-code-${this._discriminator}`,
        );
    }

    private _saveSalesTrackingKey(key: string) {
        localStorage.setItem(`sales-tracking-key-${this.discriminator}`, key);
    }

    private _loadSalesTrackingKey() {
        this._salesTrackingKey = localStorage.getItem(
            `sales-tracking-key-${this.discriminator}`,
        );
    }

    private _updatePromoCodeFromCart(cart: Cart): void {
        const itemWithDiscount: CartItem | undefined = cart.items.find(
            (i) => i.discount != null,
        );
        const promoCode =
            itemWithDiscount && itemWithDiscount.discount
                ? itemWithDiscount.discount.code
                : "";

        this._savePromoCode(promoCode);
        this._promoCode = promoCode;
    }

    private _checkRemovedTicketsFromCart(oldCart: Cart, newCart: Cart): void {
        const removedTickets = oldCart.items.filter(
            (it) => !newCart.items.some((i) => i.ticketId === it.ticketId),
        );
        if (removedTickets.length > 0) {
            this.triggerTicketsRemoved(removedTickets.map((it) => it.ticketId));
            for (const ticket of removedTickets) {
                if (ticket.eventSeatmap && ticket.sectionSeat) {
                    this.triggerSeatmapTicketRemoved(
                        ticket.eventSeatmap.id,
                        ticket.sectionSeat.id,
                    );
                }
            }
        }
    }

    // Misc client side methods
    private _hasStrathmoreCowboyTickets(): boolean {
        if (!this._cart) return false;
        return this._cart.items.some((i) => [1398].includes(i.event.id));
    }

    private _hasRanchmansConcertTickets(): boolean {
        if (!this._cart) return false;
        return this._cart.items.some((i) => [33565].includes(i.department.id));
    }

    private _hasStrathmoreConcertTickets(): boolean {
        if (!this._cart) return false;
        return this._cart.items.some((i) => [1008].includes(i.event.id));
    }

    private _hasStrathmoreChucksTickets(): boolean {
        if (!this._cart) return false;
        return (
            this._cart.items.some((i) => i.department.id === 28982) ||
            this._cart.packageItems.some((pkg) =>
                [9, 11, 12].includes(pkg.eventPackage.id),
            )
        );
    }
}

export const useCartStore = (discriminator?: string): CartStore | null => {
    if (discriminator) {
        const d = discriminator.toLowerCase();
        if (!store || store.discriminator !== d) {
            store = new CartStore(d);
        }
        if (d.startsWith("sda")) {
            store.setSdaCode(d.substring(4).toUpperCase());
        }
    }
    return store;
};

export default CartStore;
